import { CheckService } from "services/CheckService";
import './Hierarchy.css';

const DEFAULT_NUMBER_THRESHOLD = 2000000;
const DEFAULT_LENGTH_THRESHOLD = 4000;
const MAX_DECIMAL_PLACES = 5;
const MAX_MEDIA_UPLOADS = 1000;
const DEFAULT_MIN_MEDIA_UPLOADS = 1;
const DEFAULT_MAX_MEDIA_UPLOADS = 10;

const convertNumberOfDecimalPlacesToThreshold = (numberOfDecimalPlaces) => {
    if (numberOfDecimalPlaces) {
        return (1 / Math.pow(10, numberOfDecimalPlaces))
    }
    return 1;
}

// Helper functions to create common field patterns
const createNumberField = (key, title, defaultValue, minFn, maxFn, decimalPlaces = 0, additionalValidation = null) => ({
    $type: "Number",
    key,
    title,
    default: defaultValue,
    min: minFn,
    max: maxFn,
    validationRule: (settings, rule) => {
        rule.required = true;
        rule.type = "number";
        if (additionalValidation) {
            additionalValidation(settings, rule);
        }
    },
    numberOfDecimalPlaces: typeof decimalPlaces === 'function' ? decimalPlaces : () => decimalPlaces
});

const createSwitchField = (key, title, defaultValue = false, validationFn = null) => ({
    $type: "Switch",
    key,
    title,
    default: defaultValue,
    validationRule: validationFn
});

const createLengthField = (isMin) => createNumberField(
    isMin ? "minLength" : "maxLength",
    isMin ? "Minimum length" : "Maximum length",
    isMin ? 0 : 1000,
    () => 0,
    isMin ?
        (settings) => {
            // Prevent negative maxLength from causing invalid constraints
            if (settings.maxLength && settings.maxLength > 0) {
                return settings.maxLength - 1;
            }
            return DEFAULT_LENGTH_THRESHOLD;
        } :
        () => DEFAULT_LENGTH_THRESHOLD,
    0
);

const getMaxValueForField = (isMin, hasDecimals, settings) => {
    if (!isMin) {
        return DEFAULT_NUMBER_THRESHOLD;
    }

    if (!settings.maxValue) {
        return DEFAULT_NUMBER_THRESHOLD;
    }

    if (hasDecimals) {
        return +settings.maxValue - convertNumberOfDecimalPlacesToThreshold(settings.numberOfDecimalPlaces);
    }

    return settings.maxValue - 1;
};

const createValueField = (isMin, hasDecimals = false) => createNumberField(
    isMin ? "minValue" : "maxValue",
    isMin ? "Minimum value" : "Maximum value",
    isMin ? 0 : 1000,
    () => -DEFAULT_NUMBER_THRESHOLD,
    (settings) => getMaxValueForField(isMin, hasDecimals, settings),
    hasDecimals ? (settings) => settings.numberOfDecimalPlaces ?? 1 : 0,
    hasDecimals ? (settings, rule) => {
        if (settings.numberOfDecimalPlaces) {
            rule.numberOfDecimalPlaces = settings.numberOfDecimalPlaces;
        }
    } : null
);

const createMediaUploadFields = (minDefault, maxDefault) => [
    createNumberField(
        "minNumberOfMedia",
        "Minimum number of uploads",
        minDefault,
        () => 1,
        (settings) => settings.maxNumberOfMedia ? settings.maxNumberOfMedia - 1 : MAX_MEDIA_UPLOADS,
        0,
        (settings, rule) => {
            rule.unitLabel = "Files";
        }
    ),
    createNumberField(
        "maxNumberOfMedia",
        "Maximum number of uploads",
        maxDefault,
        (settings) => settings.minNumberOfMedia ? settings.minNumberOfMedia + 1 : 2,
        () => MAX_MEDIA_UPLOADS,
        0,
        (settings, rule) => {
            rule.unitLabel = "Files";
        }
    )
];

const dateValidation = (settings, rule) => {
    // Date validation is handled using external rules in HierarchyQuestion component
    // to avoid modifying core validation functionality
};

export const QuestionTypesConfiguration = {
    "IntegerQuestion": {
        fields: [
            createValueField(true),
            createValueField(false)
        ],
        displayType: "Number",
    },
    "DecimalQuestion": {
        fields: [
            createValueField(true, true),
            createValueField(false, true),
            createNumberField(
                "numberOfDecimalPlaces",
                "Number of decimal places",
                1,
                () => 1,
                () => MAX_DECIMAL_PLACES,
                0
            )
        ],
        displayType: "Number",
    },
    "TextAreaQuestion": {
        fields: [
            createLengthField(true),
            createLengthField(false)
        ],
        displayType: "TextArea",
    },
    "TextQuestion": {
        fields: [
            createLengthField(true),
            createLengthField(false)
        ],
        displayType: "Text",
    },
    "DateQuestion": {
        fields: [
            createSwitchField("preventPastDates", "Exclude past dates", false, dateValidation),
            createSwitchField("preventFutureDates", "Exclude future dates", false, dateValidation),
            createSwitchField("includeTime", "Include time", false),
            createSwitchField("defaultToTodaysDate", "Default to today's date", false)
        ],
        displayType: "Date"
    },
    "DropdownQuestion": {
        fields: [{
            $type: "Options",
            key: "options",
            title: "Add dropdown options",
            default: [],
            validationRule: (settings, rule) => {
                rule.required = true;
            },
            multipleCanBeSelectedByDefault: false
        }],
        displayType: "Dropdown"
    },
    "MultiSelectQuestion": {
        fields: [{
            $type: "Options",
            key: "options",
            title: "Add multi-select options",
            default: [],
            validationRule: (settings, rule) => {
                rule.required = true;
            },
            multipleCanBeSelectedByDefault: true
        }],
        displayType: "MultiSelect",
    },
    YesNoQuestion: {
        fields: [
            {
                $type: "BinarySelect",
                key: "binaryOptions",
                title: "Edit binary-select fields",
                defaults: [
                    { key: "labelForYes", value: "Yes" },
                    { key: "labelForNo", value: "No" },
                ],
                validationRules: (settings, rules) => {
                    rules.push({ fieldName: "labelForYes", required: true });
                    rules.push({ fieldName: "labelForNo", required: true });
                },
            },
        ],
        displayType: "YesNo",
    },
    "DefectCaptureQuestion": {
        fields: [
            ...createMediaUploadFields(1, 4),
            createSwitchField("hideCameraRoll", "Hide camera roll", true),
            createSwitchField("assetShouldBeOffRoadWhenDefectIsRaised", "Set asset to VOR when defect", true),
            {
                $type: "Suggest",
                key: "reportingCategory",
                title: "Reporting category",
                default: "",
                helperText: "Please enter a reporting category, this will be used to categorize any defects that are raised while completing the Check and it will be used in the reporting of the Check, e.g. Windscreen, Tyres",
                items: [],
                getDynamicItems: () => CheckService.getTemplateOptions().then(response => {

                    const options = response.defectReportingCategories;

                    if (!options) {
                        return [];
                    }

                    return options.map(option => ({ id: option, name: option }));
                }).catch(() => []),
                //   onFilter: (query) => {}, // Use it if will filtering when typing
                validationRule: (settings, rule) => {
                    rule.required = true;
                    rule.maxLength = 255;
                }
            },
        ],
        displayType: "DefectCapture",
    },
    "MediaCaptureQuestion": {
        fields: [
            ...createMediaUploadFields(DEFAULT_MIN_MEDIA_UPLOADS, DEFAULT_MAX_MEDIA_UPLOADS),
            createSwitchField("allowImages", "Image uploads", true),
            createSwitchField("allowVideos", "Video uploads", true),
            createSwitchField("hideCameraRoll", "Hide camera roll", false),
        ],
        displayType: "MediaCapture",
    },
    "AssetLookupQuestion": {
        fields: [],
        displayType: "AssetLookup",
    }
};
